import { useCallback } from 'react';
import axios from 'axios';

import useWfApi from './axios/useWfApi';

export interface WfOrganizationBaseOption {
  code: string;
  name: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface WfOrganizationSquadOption extends WfOrganizationBaseOption {}

export interface WfOrganizationSectionOption extends WfOrganizationBaseOption {
  location?: WfOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
  squads: WfOrganizationSquadOption[];
}

export interface WfOrganizationSalesOfficeLocation {
  longitude: number;
  latitude: number;
}

export interface WfOrganizationSalesOfficeOption extends WfOrganizationBaseOption {
  aliasBranchCode: string;
  aliasSalesOfficeName?: string;
  location: WfOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
  sections: WfOrganizationSectionOption[];
}

export interface WfOrganizationBranchOption extends WfOrganizationBaseOption {
  salesOffices: WfOrganizationSalesOfficeOption[];
}

export interface WfOrganizationOptions {
  branchOffices: WfOrganizationBranchOption[];
}

type WfOrganizationOptionsQueryParams = {
  branchCode: string;
  salesOfficeCode: string;
  sectionCode: string;
  squadCode: string;
};

export interface WfStatusOption {
  statusCode: string;
  statusName: string;
}

export interface WfStatusOptionsGroup {
  groupName: string;
  statuses: WfStatusOption[];
}

function useWfOptionsRepo() {
  const { get } = useWfApi();

  const getOrganizationOptionsRequest = useCallback(
    async (
      branchCode?: string,
      salesOfficeCode?: string,
      sectionCode?: string,
      squadCode?: string
    ) => {
      const params: Partial<WfOrganizationOptionsQueryParams> = {};
      if (branchCode) params.branchCode = branchCode;
      if (salesOfficeCode) params.salesOfficeCode = salesOfficeCode;
      if (sectionCode) params.sectionCode = sectionCode;
      if (squadCode) params.squadCode = squadCode;

      try {
        return (
          (await get<WfOrganizationOptions>('/api/wf/options/organization', {
            params: Object.keys(params).length ? params : undefined,
          })) ?? {
            branchOffices: [],
          }
        );
      } catch (error) {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          throw error;
        }
        console.log(error);
        return { branchOffices: [] };
      }
    },
    [get]
  );

  const getStatusesOptionsRequest = useCallback(async () => {
    try {
      return (await get<WfStatusOptionsGroup[]>('/api/wf/options/statuses')) ?? [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        throw error;
      }
      console.log(error);
      return [];
    }
  }, [get]);

  return {
    getOrganizationOptionsRequest,
    getStatusesOptionsRequest,
  };
}

export default useWfOptionsRepo;
